import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { usePrefs, useLabels, useRequestData } from 'wsm-common-data';
import { formatNumber, formatCurrency } from 'ddc-intl';
import { setClassNames } from 'ddc-classnames-js';
import isNil from '../util/isNil';
import { UIContext, ResultsContext } from '../contexts';

const VehicleCard = ({
	href,
	imageUrl,
	title,
	price,
	odometer,
	condition,
	city,
	state
}) => {
	const { handleLinkClick } = useContext(ResultsContext);
	const { showCityAndStateBoolean } = useContext(UIContext);
	const labels = useLabels();
	const { locale } = useRequestData();

	const { showPrice } = usePrefs();

	if (!title || !href || !imageUrl) {
		return null;
	}
	const displayTitle =
		title.length > 50 ? `${title.substring(0, 50)}...` : title;

	// Modify condition value based on its type
	let displayCondition = condition;
	if (condition === 'NEW') {
		displayCondition = labels.get('NEW').toUpperCase();
	} else if (condition === 'USED') {
		displayCondition = labels.get('PRE_OWNED').toUpperCase();
	} else if (condition === 'CERTIFIED') {
		displayCondition = labels.get('CERTIFIED').toUpperCase();
	}

	const displayOdometer = formatNumber(Number(odometer), locale) || '0';
	const mileageLabel = labels.get('MILEAGE');

	// Need to remove the comma and dollar sign to format the price if not null/undefined
	const displayPrice = formatCurrency(
		Number(price.replace(/[$,]/g, '')),
		locale,
		false
	);
	const shouldDisplayPrice = !isNil(price) && showPrice === 'true';

	const handleClick = () => {
		handleLinkClick('vehicleLink', displayTitle, '');
	};

	return (
		<a
			href={href}
			className={setClassNames([
				'd-flex',
				'border-bottom',
				'vehicle-card-horizontal',
				'list-group-item'
			])}
			aria-label={`${labels.get('VIEW_DETAILS_ABOUT')} ${title}`}
			onClick={handleClick}
		>
			<div
				data-testid="sts-vehicle-card"
				className={setClassNames([
					'd-flex',
					'justify-content-end',
					'site-search-vehicle-card-media-container',
					'flex-grow-0',
					'order-2'
				])}
			>
				<img
					className={setClassNames(['img-responsive'])}
					src={`${imageUrl}?impolicy=downsize&w=130`}
					alt={title}
				/>
			</div>
			<div
				className={setClassNames([
					'site-search-vehicle-card-details-container',
					'order-1',
					'ddc-font-size-small',
					'p-1',
					'line-height-condensed'
				])}
			>
				<p
					className={setClassNames([
						'mt-0',
						'mb-0',
						'ddc-font-size-xsmall',
						'text-muted'
					])}
				>
					{displayCondition}
				</p>
				<p
					className={setClassNames([
						'font-weight-normal',
						'mt-0',
						'mb-0'
					])}
				>
					{displayTitle}
				</p>
				{shouldDisplayPrice && (
					<p
						className={setClassNames([
							'font-weight-bold',
							'mt-0',
							'mb-0'
						])}
					>
						{displayPrice}
					</p>
				)}
				{condition.toUpperCase() !== 'NEW' && (
					<p
						className={setClassNames([
							'mt-0',
							'mb-0',
							'text-muted'
						])}
					>
						{`${mileageLabel}: ${displayOdometer}`}
					</p>
				)}
				{showCityAndStateBoolean && (
					<p
						className={setClassNames([
							'mt-2',
							'mb-0',
							'text-muted'
						])}
					>
						{`${city}, ${state}`}
					</p>
				)}
			</div>
		</a>
	);
};
VehicleCard.propTypes = {
	href: PropTypes.string.isRequired,
	imageUrl: PropTypes.string.isRequired,
	title: PropTypes.string.isRequired,
	price: PropTypes.string,
	odometer: PropTypes.string,
	condition: PropTypes.string,
	city: PropTypes.string,
	state: PropTypes.string
};

VehicleCard.defaultProps = {
	price: '',
	odometer: '',
	condition: ''
};

export default VehicleCard;
